import InspectStepOne from "components/inspect/step/inspect.step.one";
import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleContainer, Step } from "style/inspect";
import InspectHeader from "../children/inspect.header";
import { useForm } from "react-hook-form";
import InspectSurveyItem from "components/inspect/survey/inspect.survey.item";
import {
  SIMPLE_TEST_AUDIO_DATA,
  SIMPLE_TEST_DATA,
  stepIntroData,
} from "utils/datas";
import InspectSurveyAudioItem from "components/inspect/survey/inspect.servey.audio.item";
import { fetchSaveSimpleTestResult } from "api/inspect";
import { getResultTypes } from "utils/inspect.util";

interface SurveyFormData {
  answers: (number | undefined)[];
}

const StepOne = () => {
  const [step, setStep] = useState<number>(0); // 현재 스텝을 관리
  const [headerTitle, setHeaderTitle] = useState<string>("");
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("accessToken");
  const { handleSubmit, setValue, getValues } = useForm<SurveyFormData>({
    defaultValues: {
      answers: [], // ✅ 처음에 빈 배열로 시작
    },
  });

  const answerableSteps = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 13, 14, 15, 16, 17, 18,
  ];

  const handleNext = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handleAnswerSelect = (value: number) => {
    const currentAnswers = getValues("answers") || [];
    const updatedAnswers = [...currentAnswers];

    const answerIndex = answerableSteps.indexOf(step); // 현재 step이 몇 번째 응답 위치인지

    if (answerIndex === -1) {
      console.warn(`step ${step}은 답변이 필요한 단계가 아닙니다.`);
      return;
    }

    // 배열 길이를 맞춰줌
    while (updatedAnswers.length <= answerIndex) {
      updatedAnswers.push(undefined as unknown as number);
    }

    updatedAnswers[answerIndex] = value;

    setValue("answers", updatedAnswers);

    const isComplete =
      updatedAnswers.length === answerableSteps.length &&
      !updatedAnswers.includes(undefined);

    if (isComplete) {
      handleSubmit(onSubmit)();
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const backArrowHandler = () => {
    if (step === 0) {
      navigate("/inspect");
    } else {
      setStep((prevStep) => prevStep - 1);
    }
  };

  const initStep = () => {
    setStep(0);
  };

  const onSubmit = async (data: SurveyFormData) => {
    const allAnswers = data.answers;

    const generalAnswers = allAnswers.slice(0, 10); // 1~10
    const audioGroup1Answers = allAnswers.slice(10, 14); // 13~16
    const audioGroup2Answers = allAnswers.slice(14, 16); // 17~18

    const score = generalAnswers.reduce(
      (sum: number, v: number | undefined) => sum + (v ?? 0),
      0
    );
    const twoFiveScore = audioGroup1Answers.reduce(
      (sum: number, v: number | undefined) => sum + (v ?? 0),
      0
    );
    const sixSevenScore = audioGroup2Answers.reduce(
      (sum: number, v: number | undefined) => sum + (v ?? 0),
      0
    );

    // ✅ resultType 추출
    const { resultType } = getResultTypes(score, twoFiveScore, sixSevenScore);

    const payload = {
      score,
      twoFiveScore,
      sixSevenScore,
      type: resultType, // 🔁 resultType을 type에 넣음
    };

    // ✅ 로그인 안 되어 있으면 서버 요청 건너뜀
    if (!accessToken) {
      navigate("/inspect/simple/result", { state: payload });
      return;
    }

    try {
      const response = await fetchSaveSimpleTestResult(payload);
      if (response.result) {
        navigate("/inspect/simple/result", { state: payload });
      }
    } catch (error) {
      console.error(error);
      // 서버 에러가 나도 일단 결과 페이지로 이동
      navigate("/inspect/simple/result", { state: payload });
    }
  };

  useEffect(() => {
    if (step === 0) {
      setHeaderTitle("층간소음 민감도 간이 검사");
    } else {
      setHeaderTitle("");
    }
  }, [step]);

  const intro1 = stepIntroData.find((d) => d.id === 1);
  const intro2 = stepIntroData.find((d) => d.id === 2);

  return (
    <SimpleContainer>
      <InspectHeader
        onClick={backArrowHandler}
        headerTitle={headerTitle}
        step={step}
        initStep={initStep}
      />
      <AnimatePresence initial={false}>
        {step === 0 && intro1 && (
          <Step
            key={0}
            initial={{ opacity: 0, y: 20 }} // 초기 상태 (살짝 오른쪽에서 시작)
            animate={{ opacity: 1, y: 0 }} // 나타날 때
            exit={{ opacity: 0, y: -20 }} // 사라질 때 (왼쪽으로 이동하며 사라짐)
            transition={{ duration: 0.3 }} // 애니메이션 속도
            className="step-one"
          >
            <InspectStepOne
              handleNext={handleNext}
              title={intro1.title}
              description={intro1.description}
              imgPath={intro1.imgPath}
            />
          </Step>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          {step >= 1 && step <= 10 && (
            <Step
              key={step}
              initial={{ opacity: 0, y: 20 }} // 초기 상태 (살짝 오른쪽에서 시작)
              animate={{ opacity: 1, y: 0 }} // 나타날 때
              exit={{ opacity: 0, y: -20 }} // 사라질 때 (왼쪽으로 이동하며 사라짐)
              transition={{ duration: 0.3 }} // 애니메이션 속도
            >
              <InspectSurveyItem
                question={SIMPLE_TEST_DATA[step - 1].question}
                imgPath={SIMPLE_TEST_DATA[step - 1].imgPath}
                handleAnswerSelect={handleAnswerSelect}
                index={step}
              />
            </Step>
          )}

          {step === 11 && intro2 && (
            <Step
              key={11}
              initial={{ opacity: 0, y: 20 }} // 초기 상태 (살짝 오른쪽에서 시작)
              animate={{ opacity: 1, y: 0 }} // 나타날 때
              exit={{ opacity: 0, y: -20 }} // 사라질 때 (왼쪽으로 이동하며 사라짐)
              transition={{ duration: 0.3 }} // 애니메이션 속도
              className="step-one"
            >
              <InspectStepOne
                handleNext={handleNext}
                title={intro2.title}
                description={intro2.description}
                imgPath={intro2.imgPath}
              />
            </Step>
          )}
          {step >= 12 && step <= 18 && (
            <Step
              key={step}
              initial={{ opacity: 0, y: 20 }} // 초기 상태 (살짝 오른쪽에서 시작)
              animate={{ opacity: 1, y: 0 }} // 나타날 때
              exit={{ opacity: 0, y: -20 }} // 사라질 때 (왼쪽으로 이동하며 사라짐)
              transition={{ duration: 0.3 }} // 애니메이션 속도
              className="step-one"
            >
              <InspectSurveyAudioItem
                audioPath={SIMPLE_TEST_AUDIO_DATA[step - 12].audioPath}
                audioPath_default={
                  SIMPLE_TEST_AUDIO_DATA[step - 12].audioPath_default
                }
                handleAnswerSelect={handleAnswerSelect}
                index={step - 11}
                question={SIMPLE_TEST_AUDIO_DATA[step - 12].question}
                handleNext={handleNext}
              />
            </Step>
          )}
        </form>
      </AnimatePresence>
    </SimpleContainer>
  );
};

export default StepOne;
